import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Assessment,
  ClauseTreeNode,
  CreateInternalAuditInput,
  InternalAudit,
  Paginated,
  Standard,
  StandardVersion,
  StandardVersionDetail,
  UpdateInternalAuditInput,
} from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Button,
  Dialog,
  Field,
  Input,
  Select,
  Textarea,
  useToast,
} from '../../components/ui.js';
import {
  auditKey,
  auditsListKey,
  errorMessage,
  issuesOf,
  memberOptions,
  useMembers,
} from './shared.js';

/** Server defaults (routes/internal-audits.ts) — pre-filled so they are visible and editable. */
const DEFAULT_OBJECTIVES =
  'Determine whether the audited controls conform to the requirements and are effectively implemented and maintained';
const criteriaFor = (standardName: string, versionLabel: string) =>
  `${standardName} ${versionLabel} — clauses in scope; the organisation's policies, procedures and records`;

const INDEPENDENCE_HINT =
  'Auditors must not audit their own work. Record any constraint and who second-reviews the findings.';

interface FormState {
  name: string;
  standardId: string;
  versionId: string;
  assessmentId: string;
  scope: string[];
  objectives: string;
  criteria: string;
  leadAuditorId: string;
  auditees: string;
  independenceNote: string;
  plannedOn: string;
  includeEvidenceText: boolean;
  conclusion: string;
}

function initialState(audit: InternalAudit | null): FormState {
  return {
    name: audit?.name ?? '',
    standardId: '',
    versionId: audit?.standardVersionId ?? '',
    assessmentId: audit?.assessmentId ?? '',
    scope: audit?.scopeClauseIds ?? [],
    objectives: audit?.objectives ?? DEFAULT_OBJECTIVES,
    criteria: audit?.criteria ?? '',
    leadAuditorId: audit?.leadAuditorId ?? '',
    auditees: audit?.auditees ?? '',
    independenceNote: audit?.independenceNote ?? '',
    plannedOn: audit?.plannedOn ?? '',
    includeEvidenceText: audit?.includeEvidenceText ?? true,
    conclusion: audit?.conclusion ?? '',
  };
}

/** Leaf clauses beneath a node (a node without children is itself a leaf). */
function leafCount(node: ClauseTreeNode): number {
  if (node.children.length === 0) return 1;
  return node.children.reduce((n, child) => n + leafCount(child), 0);
}

const isAnnex = (node: ClauseTreeNode) => /^A\./i.test(node.clauseNumber);

/**
 * "New audit" (audit = null) and "Edit details" (audit set) share this form.
 * The standard version and scope are fixed once the audit is planned — the
 * items are generated from them — so edit mode shows them read-only.
 */
export function AuditFormDialog({
  open,
  onClose,
  audit,
}: {
  open: boolean;
  onClose: () => void;
  audit: InternalAudit | null;
}) {
  const { org } = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEdit = audit !== null;

  const [form, setForm] = useState<FormState>(() => initialState(audit));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [criteriaDefault, setCriteriaDefault] = useState('');
  // Reset when the dialog opens (not on every refetch of the audit, which
  // would wipe edits in progress).
  useEffect(() => {
    if (!open) return;
    setForm(initialState(audit));
    setErrors({});
    setCriteriaDefault('');
  }, [open, audit?.id]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Catalogue (create mode only) — the same queries AssessmentsPage uses.
  const standards = useQuery({
    queryKey: ['standards'],
    queryFn: () => apiFetch<Paginated<Standard>>('/standards', { query: { limit: 100 } }),
    enabled: open && !isEdit,
  });
  const versions = useQuery({
    queryKey: ['standard-versions', form.standardId],
    queryFn: () => apiFetch<StandardVersion[]>(`/standards/${form.standardId}/versions`),
    enabled: open && !isEdit && !!form.standardId,
  });
  const versionDetail = useQuery({
    queryKey: ['version-detail', form.versionId],
    queryFn: () => apiFetch<StandardVersionDetail>(`/standard-versions/${form.versionId}`),
    enabled: open && !isEdit && !!form.versionId,
  });
  const assessments = useQuery({
    queryKey: ['assessments', org.orgId],
    queryFn: () => apiFetch<Assessment[]>(`/orgs/${org.orgId}/assessments`),
    enabled: open,
  });
  const members = useMembers(org.orgId, open);

  const versionOptions = useMemo(
    () => (versions.data ?? []).filter((v) => v.status === 'active' || v.status === 'draft'),
    [versions.data],
  );
  const topLevel = versionDetail.data?.clauses ?? [];
  const hasAnnex = topLevel.some(isAnnex);
  const assessmentOptions = useMemo(
    () => (assessments.data ?? []).filter((a) => a.standardVersionId === form.versionId),
    [assessments.data, form.versionId],
  );
  const leadOptions = memberOptions(members.data, audit?.leadAuditorId, audit?.leadAuditorName);
  const scopeLeaves = topLevel
    .filter((n) => form.scope.includes(n.id))
    .reduce((n, node) => n + leafCount(node), 0);

  const chooseVersion = (versionId: string) => {
    const version = versionOptions.find((v) => v.id === versionId);
    const standard = standards.data?.items.find((s) => s.id === form.standardId);
    const nextDefault = version && standard ? criteriaFor(standard.name, version.label) : '';
    setForm((f) => ({
      ...f,
      versionId,
      assessmentId: '',
      scope: [],
      // Keep a criteria the user has typed; replace an untouched default.
      criteria: f.criteria === '' || f.criteria === criteriaDefault ? nextDefault : f.criteria,
      name:
        f.name.trim() === '' && version && standard
          ? `${standard.name} ${version.label} internal audit`
          : f.name,
    }));
    setCriteriaDefault(nextDefault);
  };

  const toggleScope = (id: string, checked: boolean) =>
    set('scope', checked ? [...new Set([...form.scope, id])] : form.scope.filter((x) => x !== id));
  const addScope = (nodes: ClauseTreeNode[]) =>
    set('scope', [...new Set([...form.scope, ...nodes.map((n) => n.id)])]);

  const create = useMutation({
    mutationFn: () => {
      const body: CreateInternalAuditInput = {
        name: form.name.trim(),
        standardVersionId: form.versionId,
        assessmentId: form.assessmentId || null,
        scopeClauseIds: form.scope,
        objectives: form.objectives.trim(),
        criteria: form.criteria.trim(),
        leadAuditorId: form.leadAuditorId || null,
        auditees: form.auditees,
        independenceNote: form.independenceNote,
        includeEvidenceText: form.includeEvidenceText,
        ...(form.plannedOn ? { plannedOn: form.plannedOn } : {}),
      };
      return apiFetch<InternalAudit>(`/orgs/${org.orgId}/internal-audits`, { method: 'POST', body });
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: auditsListKey(org.orgId) });
      toast(`Audit planned with ${created.progress.total} clause${created.progress.total === 1 ? '' : 's'} in scope.`, 'success');
      onClose();
      navigate(`/internal-audits/${created.id}`);
    },
    onError: (err) => {
      setErrors(issuesOf(err));
      toast(errorMessage(err, 'Could not plan the audit'), 'error');
    },
  });

  const update = useMutation({
    mutationFn: async () => {
      if (!audit) return null;
      const body: UpdateInternalAuditInput = {};
      if (form.name.trim() !== audit.name) body.name = form.name.trim();
      if (form.assessmentId !== (audit.assessmentId ?? '')) body.assessmentId = form.assessmentId || null;
      if (form.objectives !== audit.objectives) body.objectives = form.objectives;
      if (form.criteria !== audit.criteria) body.criteria = form.criteria;
      if (form.leadAuditorId !== (audit.leadAuditorId ?? '')) body.leadAuditorId = form.leadAuditorId || null;
      if (form.auditees !== audit.auditees) body.auditees = form.auditees;
      if (form.independenceNote !== audit.independenceNote) body.independenceNote = form.independenceNote;
      if (form.plannedOn !== (audit.plannedOn ?? '')) body.plannedOn = form.plannedOn || null;
      if (form.includeEvidenceText !== audit.includeEvidenceText) body.includeEvidenceText = form.includeEvidenceText;
      if (audit.status === 'completed' && form.conclusion !== audit.conclusion) body.conclusion = form.conclusion;
      if (Object.keys(body).length === 0) return audit;
      return apiFetch<InternalAudit>(`/internal-audits/${audit.id}`, { method: 'PATCH', body });
    },
    onSuccess: (updated) => {
      if (updated) {
        queryClient.setQueryData(auditKey(updated.id), updated);
        void queryClient.invalidateQueries({ queryKey: auditKey(updated.id) });
        void queryClient.invalidateQueries({ queryKey: auditsListKey(org.orgId) });
      }
      toast('Audit details saved.', 'success');
      onClose();
    },
    onError: (err) => {
      setErrors(issuesOf(err));
      toast(errorMessage(err, 'Could not save the audit details'), 'error');
    },
  });

  const pending = create.isPending || update.isPending;
  const canSubmit = isEdit
    ? form.name.trim() !== ''
    : form.name.trim() !== '' && !!form.versionId && form.scope.length > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || pending) return;
    setErrors({});
    if (isEdit) update.mutate();
    else create.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? 'Edit audit details' : 'New internal audit'}>
      <form onSubmit={submit} className="audit-form">
        <Field label="Audit name" required error={errors.name}>
          {(props) => (
            <Input
              {...props}
              required
              maxLength={200}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. ISO 27001 internal audit 2026"
            />
          )}
        </Field>

        {isEdit ? (
          <div className="field">
            <span className="field-label">Standard version</span>
            <span>
              {audit.standardName} {audit.versionLabel}
            </span>
          </div>
        ) : (
          <div className="audit-form-row">
            <Field label="Standard" required>
              {(props) => (
                <Select
                  {...props}
                  required
                  value={form.standardId}
                  onChange={(e) => {
                    set('standardId', e.target.value);
                    chooseVersion('');
                  }}
                >
                  <option value="">Choose a standard…</option>
                  {(standards.data?.items ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Version" required error={errors.standardVersionId}>
              {(props) => (
                <Select
                  {...props}
                  required
                  disabled={!form.standardId}
                  value={form.versionId}
                  onChange={(e) => chooseVersion(e.target.value)}
                >
                  <option value="">Choose a version…</option>
                  {versionOptions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                      {v.status !== 'active' ? ` (${v.status})` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        )}

        <Field
          label="Source assessment"
          hint="Responses and evidence the AI auditor examines. Leave empty to audit documents and registers only."
          error={errors.assessmentId}
        >
          {(props) => (
            <Select
              {...props}
              disabled={!form.versionId}
              value={form.assessmentId}
              onChange={(e) => set('assessmentId', e.target.value)}
            >
              <option value="">None (documents only)</option>
              {assessmentOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.status !== 'open' ? ` (${a.status})` : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {isEdit ? (
          <div className="field">
            <span className="field-label">Scope</span>
            <ul className="scope-summary">
              {audit.scope.map((s) => (
                <li key={s.clauseId}>
                  <span className="scope-number">{s.number}</span> {s.title}
                </li>
              ))}
            </ul>
            <span className="field-hint">
              The scope is fixed once the audit is planned — plan a new audit for a different scope.
            </span>
          </div>
        ) : (
          <fieldset className="scope-fieldset">
            <legend className="field-label">
              Scope
              <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>
                {' '}
                *
              </span>
            </legend>
            <span className="field-hint">
              Tick the top-level clauses to audit. Every clause beneath them becomes an item for the
              auditor.
            </span>
            {!form.versionId ? (
              <p className="audit-muted">Choose a standard version first.</p>
            ) : versionDetail.isLoading ? (
              <p className="audit-muted">Loading clauses…</p>
            ) : (
              <>
                <div className="scope-shortcuts">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => addScope(hasAnnex ? topLevel.filter((n) => !isAnnex(n)) : topLevel)}
                  >
                    Select all clauses
                  </Button>
                  {hasAnnex && (
                    <Button size="sm" variant="secondary" onClick={() => addScope(topLevel.filter(isAnnex))}>
                      Select all Annex A
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => set('scope', [])}>
                    Clear
                  </Button>
                </div>
                <ul className="scope-list" role="group" aria-label="Clauses in scope">
                  {topLevel.map((node) => {
                    const leaves = leafCount(node);
                    return (
                      <li key={node.id}>
                        <label>
                          <input
                            type="checkbox"
                            checked={form.scope.includes(node.id)}
                            onChange={(e) => toggleScope(node.id, e.target.checked)}
                          />
                          <span className="scope-number">{node.clauseNumber}</span>
                          <span className="scope-title">{node.title}</span>
                          <span className="scope-count">
                            {leaves} clause{leaves === 1 ? '' : 's'}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <span className="field-hint">
                  {form.scope.length} section{form.scope.length === 1 ? '' : 's'} · {scopeLeaves} clause
                  {scopeLeaves === 1 ? '' : 's'} in scope
                </span>
              </>
            )}
            {errors.scopeClauseIds && (
              <span className="field-error" role="alert">
                {errors.scopeClauseIds}
              </span>
            )}
          </fieldset>
        )}

        <Field label="Objectives" error={errors.objectives}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              maxLength={5000}
              value={form.objectives}
              onChange={(e) => set('objectives', e.target.value)}
            />
          )}
        </Field>
        <Field label="Criteria" hint="What the audit judges against" error={errors.criteria}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              maxLength={5000}
              value={form.criteria}
              onChange={(e) => set('criteria', e.target.value)}
            />
          )}
        </Field>

        <div className="audit-form-row">
          <Field label="Lead auditor" error={errors.leadAuditorId}>
            {(props) => (
              <Select {...props} value={form.leadAuditorId} onChange={(e) => set('leadAuditorId', e.target.value)}>
                <option value="">Not assigned</option>
                {leadOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Planned date" error={errors.plannedOn}>
            {(props) => (
              <Input {...props} type="date" value={form.plannedOn} onChange={(e) => set('plannedOn', e.target.value)} />
            )}
          </Field>
        </div>

        <Field label="Auditees / people interviewed" hint="Who is being audited and who was interviewed" error={errors.auditees}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              maxLength={5000}
              value={form.auditees}
              onChange={(e) => set('auditees', e.target.value)}
            />
          )}
        </Field>
        <Field label="Auditor independence" hint={INDEPENDENCE_HINT} error={errors.independenceNote}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              maxLength={2000}
              value={form.independenceNote}
              onChange={(e) => set('independenceNote', e.target.value)}
            />
          )}
        </Field>

        {isEdit && audit.status === 'completed' && (
          <Field label="Conclusion" hint="Recorded when the audit was completed; amend it here if needed" error={errors.conclusion}>
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                maxLength={5000}
                value={form.conclusion}
                onChange={(e) => set('conclusion', e.target.value)}
              />
            )}
          </Field>
        )}

        <div className="field">
          <label className="field-label audit-check">
            <input
              type="checkbox"
              checked={form.includeEvidenceText}
              onChange={(e) => set('includeEvidenceText', e.target.checked)}
            />
            Include evidence file contents
          </label>
          <span className="field-hint">
            The AI auditor reads the text of evidence files (documents, spreadsheets, PDFs) as well as
            their names. More thorough, but each clause takes longer and uses more of the AI budget.
          </span>
        </div>

        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" loading={pending} disabled={!canSubmit}>
            {isEdit ? 'Save details' : 'Plan audit'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
